import {ErrorSeverity} from '../types/AnalyticsConfig';

/**
 * This class is used to limit error reporting for identical consecutive error codes and severities per session.
 *
 * Repeated consecutive errors of the same code and severity are limited to {@link IdenticalErrorReportingLimiter.MAX_IDENTICAL_ERROR_REPORTING}.
 */
export class IdenticalErrorReportingLimiter {
  /** The maximum number of times the same error code and severity can be reported consecutively. */
  private static MAX_IDENTICAL_ERROR_REPORTING = 5;

  private _lastErrorCode: number | undefined;
  private _lastErrorSeverity: ErrorSeverity | undefined;
  private _currentErrorCount = 0;

  /**
   * Determines whether an error with the given errorCode and errorSeverity should be reported,
   * based on the limit of identical consecutive error reports.
   *
   * @param errorCode - The error code for which we are checking the reporting conditions.
   * @param errorSeverity - The error severity for which we are checking the reporting conditions.
   * @returns {boolean} - Returns true if the error can be reported, false if it exceeds the limits.
   */
  shouldReportError(errorCode: number, errorSeverity: ErrorSeverity): boolean {
    if (errorCode === this._lastErrorCode && errorSeverity === this._lastErrorSeverity) {
      this._currentErrorCount++;
      return this._currentErrorCount <= IdenticalErrorReportingLimiter.MAX_IDENTICAL_ERROR_REPORTING;
    }

    this._lastErrorCode = errorCode;
    this._lastErrorSeverity = errorSeverity;
    this._currentErrorCount = 1;
    return true;
  }

  reset() {
    this._lastErrorCode = undefined;
    this._lastErrorSeverity = undefined;
    this._currentErrorCount = 0;
  }
}
